import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { VerydiaWordmark } from './brand.js';

/* -------------------------------------------------------------------------
   AppShell — persistent sidebar, context bar, scrolling content.

   The shell owns page scroll rather than the document: navigation and context
   must never scroll away, because "which environment am I in" has to be
   answerable at any scroll position (§7).
   ------------------------------------------------------------------------- */

export interface NavItem {
  label: string;
  href: string;
  icon?: ReactNode;
}

export interface NavGroup {
  /** Group heading. Omitted for the top-level group so it does not read as a category. */
  label?: string;
  items: readonly NavItem[];
}

export interface AppShellProps {
  navigation: readonly NavGroup[];
  /** Route-aware link component (TanStack Router's Link in the console). */
  linkComponent: React.ElementType;
  /** Returns true when `href` is the active route. */
  isActive: (href: string) => boolean;
  /** Organization / product / environment switchers. */
  contextBar: ReactNode;
  /** Right-hand controls: theme toggle, docs link, account. */
  topBarActions?: ReactNode;
  /** Full-width strip above everything — used for the VC-0 stage notice. */
  banner?: ReactNode;
  children: ReactNode;
}

export function AppShell({
  navigation,
  linkComponent: Link,
  isActive,
  contextBar,
  topBarActions,
  banner,
  children,
}: AppShellProps) {
  return (
    <div className="flex h-dvh w-full overflow-hidden bg-[var(--vc-bg)]">
      {/* Skip link: the sidebar is ~20 tab stops deep, so keyboard users need a
          way past it on every page (§33). */}
      <a
        href="#vc-main"
        className={clsx(
          'sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100]',
          'focus:rounded-[var(--vc-radius-md)] focus:bg-[var(--vc-accent)] focus:px-3 focus:py-2',
          'focus:text-[13px] focus:font-medium focus:text-[var(--vc-accent-contrast)]',
        )}
      >
        Skip to main content
      </a>

      <Sidebar navigation={navigation} linkComponent={Link} isActive={isActive} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className={clsx(
            'flex shrink-0 items-center gap-3 border-b border-[var(--vc-border)]',
            'bg-[var(--vc-surface)] px-4',
          )}
          style={{ height: 'var(--vc-topbar-height)' }}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1">{contextBar}</div>
          {topBarActions ? (
            <div className="flex shrink-0 items-center gap-1">{topBarActions}</div>
          ) : null}
        </header>

        {banner}

        <main id="vc-main" tabIndex={-1} className="min-w-0 flex-1 overflow-y-auto outline-none">
          <div className="mx-auto w-full max-w-[1360px] px-6 py-6">{children}</div>
        </main>
      </div>
    </div>
  );
}

function Sidebar({
  navigation,
  linkComponent: Link,
  isActive,
}: {
  navigation: readonly NavGroup[];
  linkComponent: React.ElementType;
  isActive: (href: string) => boolean;
}) {
  return (
    <nav
      aria-label="Primary"
      className={clsx(
        'hidden shrink-0 flex-col border-r border-[var(--vc-nav-border)]',
        'bg-[var(--vc-nav-bg)] md:flex',
      )}
      style={{ width: 'var(--vc-sidebar-width)' }}
    >
      <div
        className="flex shrink-0 items-center border-b border-[var(--vc-nav-border)] px-4"
        style={{ height: 'var(--vc-topbar-height)' }}
      >
        <VerydiaWordmark />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-3">
        {navigation.map((group, groupIndex) => (
          <div key={group.label ?? `group-${groupIndex}`} className={groupIndex > 0 ? 'mt-5' : ''}>
            {group.label ? (
              <h2
                className={clsx(
                  'px-2 pb-1.5 text-[10px] font-semibold tracking-[0.08em]',
                  'text-[var(--vc-nav-text-muted)] uppercase',
                )}
              >
                {group.label}
              </h2>
            ) : null}

            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      href={item.href}
                      aria-current={active ? 'page' : undefined}
                      className={clsx(
                        'flex items-center gap-2.5 rounded-[var(--vc-radius-md)] px-2 py-[7px]',
                        'text-[13px] transition-colors duration-[var(--vc-duration-fast)]',
                        'focus-visible:outline-2 focus-visible:outline-offset-2',
                        'focus-visible:outline-[var(--vc-nav-accent)]',
                        active
                          ? 'bg-[var(--vc-nav-active)] font-medium text-[var(--vc-nav-text-strong)]'
                          : 'text-[var(--vc-nav-text)] hover:bg-[var(--vc-nav-hover)] hover:text-[var(--vc-nav-text-strong)]',
                      )}
                    >
                      {item.icon ? (
                        <span
                          aria-hidden="true"
                          className={clsx(
                            'shrink-0 [&>svg]:size-4',
                            active
                              ? 'text-[var(--vc-nav-accent)]'
                              : 'text-[var(--vc-nav-text-muted)]',
                          )}
                        >
                          {item.icon}
                        </span>
                      ) : null}
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------------
   StageBanner

   §30 forbids presenting an unbuilt product as a working one. This strip is the
   standing, unmissable statement of what this deployment actually is.
   ------------------------------------------------------------------------- */

export function StageBanner({ children }: { children: ReactNode }) {
  return (
    /*
      `role="note"`, not `role="status"`.

      `status` is an implicit live region: it means "this just changed, announce
      it". This banner never changes — it is standing page furniture. Marked as
      a status it was read aloud in full on every navigation, forty words about
      milestone scope ahead of the page the operator actually asked for, and it
      sat in front of the genuine live announcements as the first such region in
      the document.

      `note` says what it is: an aside, available when you reach it, silent
      until then.
    */
    <div
      role="note"
      className={clsx(
        'flex shrink-0 items-center gap-2 border-b border-[var(--vc-accent-subtle-border)]',
        'bg-[var(--vc-accent-subtle-bg)] px-4 py-1.5',
        'text-[12px] text-[var(--vc-accent-subtle-text)]',
      )}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="size-3.5 shrink-0">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 11v5m0-8.2v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <span className="min-w-0">{children}</span>
    </div>
  );
}
